/**
 * Output Panel — v8: Backend-driven configuration.
 * SOURCE_LABELS from ConfigContext (backend), not local JS imports.
 * Zone C is a compact decision summary; Funding accordion is the editable detail
 * (Zone C collapses while Funding is open to avoid triple-telling).
 */
import { useState, useRef, useLayoutEffect } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { formatMoney, formatPercent, formatFxChip } from '../utils/formatters';
import { ValueWithTooltip } from './Tooltip';
import { useConfig } from '../context/ConfigContext';
import { useDealCurrency } from '../context/DealCurrencyContext';
import { WarningCircle, ArrowCounterClockwise } from '@phosphor-icons/react';
import { countryFlagEmoji, FALLBACK_COUNTRY_PRESETS } from '../utils/countryPresets';
import { DocumentsSection } from './DocumentsSection';
import { PlWaterfallChart } from './PlWaterfallChart';
import { draftOnFocus, isEffectivelyZero } from '../utils/clearableNumber';
import { EditableOverrideRow, SourceSelect } from './pnl/EditableOverrideRow';
import { ClearableNumberInput } from './ClearableNumberInput';
import { applyFieldOverride, applyCapitalSource, applyBankDebtAssumption } from '../utils/pnlInputMutations';
import {
  isFundingCoverageOk,
  fundingGapChf,
  fillFundingGapOverrides,
  ownershipSplit,
  zeroEquityWithPartner,
} from '../utils/fundingClarity';

const GRID = "grid grid-cols-[1fr_44px_82px] items-center";

function Metric({ label, value, fieldKey, className = '' }) {
  return (
    <div className="flex justify-between items-center leading-none" style={{ paddingTop: '1px', paddingBottom: '1px' }}>
      <span className="text-[8px] text-gray-500 uppercase leading-none">{label}</span>
      <ValueWithTooltip fieldKey={fieldKey}>
        <span className={`text-[10px] font-semibold tabular-nums leading-none ${className}`}>{value}</span>
      </ValueWithTooltip>
    </div>
  );
}

/** CHF reporting equivalent — readable badge (formatReport already prefixes CHF). */
function ChfEquivalent({ value, formatReport, testId, className = '' }) {
  if (value == null || Number.isNaN(value)) return null;
  return (
    <div
      className={`text-[8px] text-sky-900 bg-sky-100 border border-sky-300/70 rounded px-1 py-0.5 text-right tabular-nums font-bold leading-tight mb-0.5 ${className}`}
      data-testid={testId}
    >
      {formatReport(value)}
    </div>
  );
}

/** Money row: deal currency primary; CHF badge when FX active (hidden for local CHF deals). */
function OpPrimaryMetric({
  label,
  fieldKey,
  className = '',
  money,
  formatReport,
  formatOp,
  report,
  testId,
}) {
  const opVal = money.opVal(fieldKey);
  const chfVal = report(fieldKey);
  return (
    <div className="mb-px">
      <Metric
        label={label}
        value={formatOp(opVal)}
        fieldKey={fieldKey}
        className={className}
      />
      {money.isFxActive && chfVal != null && (
        <ChfEquivalent
          value={chfVal}
          formatReport={formatReport}
          testId={testId || `pl-${fieldKey}-chf`}
        />
      )}
    </div>
  );
}

/** Dashboard profit row: CHF reporting primary; operating amount as readable subtitle when FX. */
function ChfPrimaryMetric({
  label,
  fieldKey,
  className = '',
  money,
  formatReport,
  formatOp,
  report,
  testId,
}) {
  const chfVal = report(fieldKey);
  const opVal = money.opVal(fieldKey);
  return (
    <div className="mb-px">
      <Metric
        label={label}
        value={formatReport(chfVal)}
        fieldKey={fieldKey}
        className={className}
      />
      {money.isFxActive && (
        <div
          className="text-[8px] text-slate-700 bg-slate-100 border border-slate-300/70 rounded px-1 py-0.5 text-right tabular-nums font-bold leading-tight mb-0.5"
          data-testid={testId || `pl-${fieldKey}-ops`}
        >
          {formatOp(opVal)}
        </div>
      )}
    </div>
  );
}

/** Top dashboard — profit waterfall; CHF primary reporting with ops subtitle when FX. */
function ProfitSummaryCard({
  money,
  formatReport,
  formatOp,
  report,
  taxLabel,
  outputs,
  partnerShareActive,
  opCur,
  showFxCaption,
}) {
  const patPositive = (report('profitAfterTax') ?? money.opVal('profitAfterTax') ?? 0) >= 0;
  const netPositive = (report('finalNetProfit') ?? money.opVal('finalNetProfit') ?? 0) >= 0;
  return (
    <div className="bg-white border border-emerald-200 rounded p-1 min-w-0" data-testid="pl-summary-dashboard">
      <div className="text-[7px] font-bold text-emerald-800 uppercase mb-px flex justify-between items-center gap-1">
        <span>Profit Summary (CHF)</span>
        {showFxCaption ? (
          <span className="normal-case font-medium text-sky-700 tabular-nums text-[7px] shrink-0" data-testid="pl-summary-fx-caption">
            {opCur} below
          </span>
        ) : (
          <span className="normal-case text-gray-500 text-[7px]" data-testid="pl-summary-single-currency">
            CHF
          </span>
        )}
      </div>
      <ChfPrimaryMetric label="Revenue" fieldKey="totalRevenue" money={money} formatReport={formatReport} formatOp={formatOp} report={report} />
      <ChfPrimaryMetric label="Dev Cost" fieldKey="totalDevCost" money={money} formatReport={formatReport} formatOp={formatOp} report={report} />
      <div className="border-t border-gray-100" style={{ marginTop: '1px', paddingTop: '1px' }}>
        <ChfPrimaryMetric label="PBT" fieldKey="profitBeforeTax" money={money} formatReport={formatReport} formatOp={formatOp} report={report} />
        <ChfPrimaryMetric
          label={`${taxLabel} ${(outputs.taxRate * 100).toFixed(1)}%`}
          fieldKey="taxAmount"
          money={money}
          formatReport={formatReport}
          formatOp={formatOp}
          report={report}
        />
        <ChfPrimaryMetric
          label="PAT"
          fieldKey="profitAfterTax"
          money={money}
          formatReport={formatReport}
          formatOp={formatOp}
          report={report}
          className={`font-bold ${patPositive ? 'text-emerald-700' : 'text-red-600'}`}
        />
        <ChfPrimaryMetric label="Fin. Cost" fieldKey="totalFinancingCost" money={money} formatReport={formatReport} formatOp={formatOp} report={report} />
        {partnerShareActive && (
          <ChfPrimaryMetric
            label={`Partner (${(outputs.pcProfitShare * 100).toFixed(0)}%)`}
            fieldKey="profitShareAmount"
            money={money}
            formatReport={formatReport}
            formatOp={formatOp}
            report={report}
            className="text-purple-700"
          />
        )}
        <ChfPrimaryMetric
          label={partnerShareActive ? 'Sponsor Net' : 'Net'}
          fieldKey="finalNetProfit"
          money={money}
          formatReport={formatReport}
          formatOp={formatOp}
          report={report}
          className={netPositive ? 'font-bold text-emerald-800' : 'text-red-600 font-bold'}
        />
        <Metric label="Margin" value={formatPercent(outputs.marginPercent)} fieldKey="marginPercent" />
      </div>
    </div>
  );
}

/** Profit section — always visible at the bottom of the linear P&L rollout. */
function PlProfitBox({
  money,
  formatReport,
  formatOp,
  report,
  taxLabel,
  outputs,
  partnerShareActive,
}) {
  const patPositive = (money.opVal('profitAfterTax') ?? 0) >= 0;
  const netPositive = (money.opVal('finalNetProfit') ?? 0) >= 0;
  return (
    <div
      className="border-t-2 border-emerald-300 bg-emerald-50/40 px-1.5 py-1"
      data-testid="pl-profit-box"
    >
      <div className="text-[7px] font-bold uppercase text-emerald-900 mb-0.5 tracking-wide flex justify-between items-center">
        <span>Profit ({money.op})</span>
        {money.isFxActive ? (
          <span className="text-[7px] font-normal normal-case text-sky-700 tabular-nums">
            CHF below
          </span>
        ) : (
          <span className="text-[7px] font-normal normal-case text-gray-500">
            {money.op} deal
          </span>
        )}
      </div>
      <OpPrimaryMetric label="PBT" fieldKey="profitBeforeTax" money={money} formatReport={formatReport} formatOp={formatOp} report={report} />
      <OpPrimaryMetric
        label={`${taxLabel} ${(outputs.taxRate * 100).toFixed(1)}%`}
        fieldKey="taxAmount"
        money={money}
        formatReport={formatReport}
        formatOp={formatOp}
        report={report}
      />
      <OpPrimaryMetric
        label="PAT"
        fieldKey="profitAfterTax"
        money={money}
        formatReport={formatReport}
        formatOp={formatOp}
        report={report}
        className={`font-bold ${patPositive ? 'text-emerald-700' : 'text-red-600'}`}
      />
      <OpPrimaryMetric label="Fin. Cost" fieldKey="totalFinancingCost" money={money} formatReport={formatReport} formatOp={formatOp} report={report} />
      {partnerShareActive && (
        <OpPrimaryMetric
          label={`Partner (${(outputs.pcProfitShare * 100).toFixed(0)}%)`}
          fieldKey="profitShareAmount"
          money={money}
          formatReport={formatReport}
          formatOp={formatOp}
          report={report}
          className="text-purple-700"
        />
      )}
      <OpPrimaryMetric
        label={partnerShareActive ? 'Sponsor Net' : 'Net'}
        fieldKey="finalNetProfit"
        money={money}
        formatReport={formatReport}
        formatOp={formatOp}
        report={report}
        className={netPositive ? 'font-bold text-emerald-800' : 'text-red-600 font-bold'}
      />
      <Metric label="Margin" value={formatPercent(outputs.marginPercent)} fieldKey="marginPercent" />
    </div>
  );
}



function Row({ label, value, fieldKey, sub = false, className = '', currency }) {
  const money = useDealCurrency();
  const cur = currency || money.op;
  return (
    <div className={`${GRID} leading-none ${sub ? 'pl-1.5' : ''}`} style={{ paddingTop: '1px', paddingBottom: '1px' }}>
      <span className={`text-[9px] leading-none ${sub ? 'text-gray-400' : 'text-gray-700'}`}>{label}</span>
      <span />
      <ValueWithTooltip fieldKey={fieldKey}>
        <span className={`block text-[9px] tabular-nums text-right leading-tight ${className}`}>{formatMoney(value, cur)}</span>
      </ValueWithTooltip>
    </div>
  );
}

function AssumptionMoneyRow({ label, value, onChange, currency, testId }) {
  return (
    <div className={`${GRID} leading-none`} style={{ paddingTop: '1px', paddingBottom: '1px' }}>
      <span className="text-[9px] leading-none text-gray-700">{label}</span>
      <span />
      <ClearableNumberInput
        value={value ?? 0}
        onChange={onChange}
        min={0}
        step={100000}
        aria-label={label}
        data-testid={testId}
        className="h-5 w-full rounded border-gray-200 bg-gray-50 px-1 text-right text-[9px] tabular-nums"
      />
      <span className="sr-only">{currency}</span>
    </div>
  );
}

function TotalRow({ label, value, fieldKey, currency }) {
  const money = useDealCurrency();
  const cur = currency || money.op;
  return (
    <div className={`${GRID} font-semibold border-t border-gray-100 leading-none`} style={{ paddingTop: '2px', paddingBottom: '1px', marginTop: '1px' }}>
      <span className="text-[9px] text-gray-900 leading-none">{label}</span>
      <span />
      <ValueWithTooltip fieldKey={fieldKey}>
        <span className="block text-[9px] tabular-nums text-right text-gray-900 leading-tight">{formatMoney(value, cur)}</span>
      </ValueWithTooltip>
    </div>
  );
}

function SectionLabel({ children }) {
  return <div className="text-[7px] text-gray-400 uppercase tracking-wide font-bold leading-none" style={{ marginTop: '3px', marginBottom: '1px' }}>{children}</div>;
}

function ResetButton({ onClick, label = "Reset", title }) {
  return (
    <button onClick={onClick}
      className="flex items-center gap-0.5 text-[7px] text-gray-400 hover:text-blue-600 px-0.5 rounded hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-blue-400"
      title={title || label}
      aria-label={title || label}
      data-testid="reset-button">
      <ArrowCounterClockwise weight="bold" className="w-2.5 h-2.5" />{label}
    </button>
  );
}

function PricePositionBadge({ position }) {
  const c = { premium: 'bg-purple-100 text-purple-700', high: 'bg-blue-100 text-blue-700', market: 'bg-gray-100 text-gray-600', competitive: 'bg-green-100 text-green-700', aggressive: 'bg-amber-100 text-amber-700' };
  return <span className={`px-1 py-px text-[6px] font-bold rounded ${c[position] || c.market}`}>{position}</span>;
}

function ProductCard({ outputs, formatOp, opCur }) {
  return (
    <div className="bg-white border border-gray-200 rounded p-1 min-w-0" data-testid="product-box">
      <div className="text-[7px] font-bold text-gray-400 uppercase mb-px flex justify-between">
        <span>Product</span>
        <PricePositionBadge position={outputs.pricePositioning} />
      </div>
      <Metric label="Units" value={<span>{outputs.numberOfUnits}{outputs.unitsAutoCalculated && <span className="text-[6px] text-blue-500 ml-0.5 font-bold">auto</span>}</span>} fieldKey="numberOfUnits" />
      <Metric label="Avg m2" value={
        <span className={outputs.avgSqmPerUnit >= 100 && outputs.avgSqmPerUnit <= 120 ? 'text-emerald-600' : outputs.avgSqmPerUnit >= 95 ? 'text-amber-600' : 'text-red-600'}>
          {outputs.avgSqmPerUnit.toFixed(0)} m2
        </span>
      } fieldKey="avgSqmPerUnit" />
      <Metric label="Unit Price" value={formatOp(outputs.pricePerUnitWithParking)} fieldKey="pricePerUnitWithParking" />
      <div className="flex justify-between items-center leading-none" style={{ paddingTop: '1px', paddingBottom: '1px' }}>
        <span className="text-[8px] text-gray-500 uppercase leading-none">Range</span>
        <span className="text-[8px] tabular-nums text-gray-500">{formatOp(outputs.pricePerUnitMin)} – {formatOp(outputs.pricePerUnitMax)}</span>
      </div>
      <Metric label={`Price/${opCur === 'CHF' ? 'm2' : `m2 (${opCur})`}`} value={formatOp(outputs.pricePerSqmEffective)} fieldKey="pricePerSqmEffective" />
      <Metric label="Sell. Area" value={<span>{outputs.netSaleableArea.toFixed(0)} m2</span>} fieldKey="netSaleableArea" />
    </div>
  );
}

export function OutputPanel({
  outputs,
  outputsChf = {},
  fx = null,
  taxLabel = 'IBGI',
  operatingCurrency = 'CHF',
  countryCode = 'CH',
  warnings,
  inputs: inputsProp,
  onInputChange,
  assumptions,
  onAssumptionsChange,
  backendValidated,
  notes,
  onNotesChange,
  dealId,
  onSave,
  readOnly = false,
}) {
  const inputs = inputsProp || {};
  const money = useDealCurrency();
  const opCur = money.op;
  const country = money.countryCode;
  const showFxCaption = money.fx && money.fx.rate != null && money.fx.base !== money.fx.quote;
  const { sourceLabels: SOURCE_LABELS, sampleInputs, countryPresets } = useConfig();
  const presets = (countryPresets && Object.keys(countryPresets).length)
    ? countryPresets
    : FALLBACK_COUNTRY_PRESETS;
  const countryName = (presets[country]?.label) || country;
  const fxChip = formatFxChip(money.fx);
  // reportVal / formatReport — P&L Summary only (CHF SSOT reporting surface)
  const report = (key) => money.reportVal(key);
  const formatReport = money.formatReport;
  const formatOp = money.formatOp;
  const canEdit = Boolean(onInputChange) && !readOnly;
  const canEditAssumptions = Boolean(onAssumptionsChange) && !readOnly;
  const canEditNotes = Boolean(onNotesChange) && !readOnly;
  const notesRef = useRef(null);

  // Grow Notes downward with content so the full text stays visible without an inner scroll.
  useLayoutEffect(() => {
    const el = notesRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 60)}px`;
  }, [notes]);

  const handleOverride = (fieldKey, override) => {
    if (!canEdit) return;
    onInputChange((prev) => applyFieldOverride(prev, fieldKey, override));
  };
  const resetOverrides = (keys) => {
    if (!canEdit) return;
    onInputChange((prev) => {
      const newOverrides = { ...(prev.overrides || {}) };
      keys.forEach((k) => { delete newOverrides[k]; });
      return { ...prev, overrides: newOverrides };
    });
  };
  const resetAllOverrides = () => {
    if (!canEdit) return;
    onInputChange((prev) => ({ ...prev, overrides: {} }));
  };

  const handleSourceChange = (field, source) => {
    if (!canEdit) return;
    onInputChange((prev) => applyCapitalSource(prev, field, source));
  };

  const fundingKeys = ['equityRequired', 'bankDebt', 'privateCapital'];
  const allFundingKeys = [...fundingKeys, 'upfrontFees'];
  const overrideHandler = canEdit ? handleOverride : undefined;
  const hasOverrides = Object.keys(inputs.overrides || {}).length > 0;
  const costKeys = ['notaryFees', 'otherLandFees', 'constructionCost', 'constructionReserve', 'architectureCost', 'consultantsCost', 'contingencyCost', 'commercializationCost', 'pilotageCost', 'taxAmount'];
  const coverageOk = isFundingCoverageOk(outputs.fundingCoverageRatio);
  const gapChf = fundingGapChf(outputs.fundingTotal, outputs.totalDevCost);

  const resetFundingAndCapital = () => {
    if (!canEdit) return;
    onInputChange((prev) => {
      const newOverrides = { ...(prev.overrides || {}) };
      allFundingKeys.forEach((k) => { delete newOverrides[k]; });
      return {
        ...prev,
        overrides: newOverrides,
        capitalSources: { ...(sampleInputs?.capitalSources || {}) },
      };
    });
  };

  const fillFundingGap = () => {
    if (!canEdit) return;
    const plans = fillFundingGapOverrides({
      fundingTotal: outputs.fundingTotal,
      totalDevCost: outputs.totalDevCost,
      bankDebtAmount: outputs.bankDebtAmount,
      equityRequired: outputs.equityRequired,
      privateCapitalAmount: outputs.privateCapitalAmount,
    });
    if (!plans.length) return;
    onInputChange((prev) => {
      const overrides = { ...(prev.overrides || {}) };
      plans.forEach((plan) => {
        overrides[plan.fieldKey] = plan.override;
      });
      return { ...prev, overrides };
    });
  };

  const fillGapPlans = fillFundingGapOverrides({
    fundingTotal: outputs.fundingTotal,
    totalDevCost: outputs.totalDevCost,
    bankDebtAmount: outputs.bankDebtAmount,
    equityRequired: outputs.equityRequired,
    privateCapitalAmount: outputs.privateCapitalAmount,
  });
  const fillGapPrimaryLabel = fillGapPlans[0]?.label || 'funding';

  const useDevBasesForFunding = () => {
    if (!canEdit) return;
    onInputChange((prev) => ({
      ...prev,
      capitalSources: {
        ...(prev.capitalSources || {}),
        equity: 'totalDevCost',
        bankDebt: 'totalDevCost',
        privateCapital: 'totalDevCost',
      },
    }));
  };

  const hasPC = outputs.privateCapitalAmount > 0;
  const pcIsEquity = outputs.privateCapitalType === 'equity';
  const partnerLabel = pcIsEquity ? 'Partner capital' : 'Private';
  const partnerShareConfigured = pcIsEquity && (outputs.pcProfitShare || 0) > 0;
  const partnerShareActive = partnerShareConfigured && hasPC && (outputs.profitShareAmount || 0) > 0;
  const partnerReturnOnCapital = hasPC && outputs.privateCapitalAmount > 0
    ? outputs.profitShareAmount / outputs.privateCapitalAmount
    : null;
  const split = ownershipSplit(outputs.equityRequired, outputs.privateCapitalAmount);
  const showZeroEquityCaveat = zeroEquityWithPartner({
    equityRequired: outputs.equityRequired,
    privateCapitalAmount: outputs.privateCapitalAmount,
    pcIsEquity,
  });
  const [fundingAccordion, setFundingAccordion] = useState([]);

  return (
    <main className="flex-1 min-w-0 bg-gray-50 p-2 md:p-1 overflow-y-auto" data-testid="output-panel">

      {/* Retained country / currency params on the sheet */}
      <div
        className="mb-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 px-1.5 py-1 rounded border border-emerald-200 bg-emerald-50/70"
        data-testid="sheet-country-params"
      >
        <span className="text-[16px] leading-none" aria-hidden="true">{countryFlagEmoji(country)}</span>
        <span className="text-[10px] font-semibold text-emerald-900" data-testid="sheet-country-label">{countryName} ({country})</span>
        <span className="text-[9px] text-emerald-800 tabular-nums">
          Operating <span className="font-bold">{opCur}</span>
        </span>
        <span className="text-[9px] text-emerald-800 tabular-nums">
          Report <span className="font-bold">CHF</span>
        </span>
        <span className="text-[9px] text-emerald-800">
          Tax <span className="font-bold">{taxLabel}</span>
        </span>
        {fxChip && (
          <span className="text-[9px] tabular-nums text-emerald-700 font-medium" data-testid="sheet-fx-chip">{fxChip}</span>
        )}
        {!fxChip && money.fx?.rate != null && (
          <span className="text-[9px] tabular-nums text-emerald-700">1 {opCur} = {Number(money.fx.rate).toFixed(4)} CHF</span>
        )}
      </div>

      {/* ═══ Top dashboard: 4 blocks in one row ═══ */}
      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-1 mb-1 md:mb-0.5"
        data-testid="structure-summary-cards"
      >
        <ProfitSummaryCard
          money={money}
          formatReport={formatReport}
          formatOp={formatOp}
          report={report}
          taxLabel={taxLabel}
          outputs={outputs}
          partnerShareActive={partnerShareActive}
          opCur={opCur}
          showFxCaption={showFxCaption}
        />
        <ProductCard outputs={outputs} formatOp={formatOp} opCur={opCur} />
        <div className="bg-white border border-gray-200 rounded p-1 min-w-0" data-testid="capital-invested-box">
          <div className="text-[7px] font-bold text-gray-500 uppercase mb-px">Capital Invested ({opCur})</div>
          <Metric label="Equity (you)" value={formatOp(outputs.equityRequired)} fieldKey="equityRequired" />
          <Metric label="Fees" value={formatOp(outputs.upfrontFees)} fieldKey="upfrontFees" />
          <Metric label="Feasib." value={formatOp(outputs.feasibilityStudy)} fieldKey="feasibilityStudy" />
          <Metric label="Fin. Costs" value={formatOp(outputs.totalFinancingCost)} fieldKey="totalFinancingCost" />
          <div className="border-t border-gray-100" style={{ marginTop: '1px', paddingTop: '1px' }}>
            <Metric label="Total" value={formatOp(outputs.capitalInvested)} fieldKey="capitalInvested" className="text-gray-900 font-bold" />
            <div className="text-[6px] text-gray-400 italic text-right leading-none">ROI denominator</div>
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded p-1 min-w-0" data-testid="funding-box">
          <div className="text-[7px] font-bold text-gray-500 uppercase mb-px flex justify-between">
            <span>Funding ({opCur})</span>
            <span className={`text-[8px] font-bold ${coverageOk ? 'text-emerald-600' : 'text-amber-600'}`}>
              {(outputs.fundingCoverageRatio * 100).toFixed(0)}%
            </span>
          </div>
          <Metric label="Equity (you)" value={formatMoney(outputs.equityRequired, opCur)} fieldKey="equityRequired" />
          <Metric label="Bank Debt" value={formatMoney(outputs.bankDebtAmount, opCur)} fieldKey="bankDebt" />
          <Metric label="Construction Facility" value={formatMoney(outputs.constructionFacilityAmount, opCur)} fieldKey="constructionFacilityAmount" />
          <Metric
            label={partnerLabel}
            value={formatMoney(outputs.privateCapitalAmount, opCur)}
            fieldKey="privateCapital"
            className={pcIsEquity ? 'text-purple-700' : undefined}
          />
          <div className="border-t border-gray-100" style={{ marginTop: '1px', paddingTop: '1px' }}>
            <Metric label="Total" value={formatMoney(outputs.fundingTotal, opCur)} fieldKey="fundingTotal" className="font-bold" />
          </div>

          <div className="mt-1 pt-1 border-t border-amber-200" data-testid="cost-of-capital-box">
            <div className="text-[7px] font-bold text-amber-600 uppercase mb-px flex justify-between">
              <span>Cost of Capital</span>
              <span className="text-[6px] font-medium text-gray-400 normal-case">{outputs.financingLabel}</span>
            </div>
            {(outputs.bankDebtAmount > 0 || outputs.constructionFacilityAmount > 0) && (
              <Metric label="Bank Int." value={formatOp(outputs.bankInterestCost)} fieldKey="bankInterestCost" />
            )}
            {outputs.bankSetupCost > 0 && (
              <Metric label="Bank Fees" value={formatOp(outputs.bankSetupCost)} fieldKey="bankSetupCost" />
            )}
            {outputs.privateDebtCost > 0 && (
              <Metric label="Priv. Int." value={formatOp(outputs.privateDebtCost)} fieldKey="privateDebtCost" />
            )}
            {outputs.privateDebtFees > 0 && (
              <Metric label="Priv. Fees" value={formatOp(outputs.privateDebtFees)} fieldKey="privateDebtFees" />
            )}
            {outputs.equityStructuringCost > 0 && (
              <Metric label="Eq. Fees" value={formatOp(outputs.equityStructuringCost)} fieldKey="equityStructuringCost" />
            )}
            <div className="border-t border-amber-100" style={{ marginTop: '1px', paddingTop: '1px' }}>
              <Metric label="Total" value={formatOp(outputs.totalFinancingCost)} fieldKey="totalFinancingCost" className="text-amber-700 font-bold" />
            </div>
            <div className="text-[6px] text-amber-500/80 italic text-right leading-none mt-0.5">Interest &amp; fees only</div>
          </div>
        </div>
      </div>

      {partnerShareConfigured && (
        <div className="bg-white border border-purple-200 rounded p-1 mb-1 md:mb-0.5" data-testid="partner-dilution-box">
          <div className="text-[7px] font-bold text-purple-700 uppercase mb-px flex justify-between">
            <span>Partner Dilution</span>
            <span className="text-[6px] font-medium text-purple-400 normal-case">
              {(outputs.pcProfitShare * 100).toFixed(0)}% of net
            </span>
          </div>
          {!hasPC ? (
            <div className="text-[8px] text-amber-700 leading-snug" data-testid="partner-share-inactive-hint">
              Inactive: set partner amount under <span className="font-semibold">Funding → Partner capital</span>. Equity is sponsor capital at risk; partner share does not reduce PBT/{taxLabel}.
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-1">
              <Metric label="Partner capital" value={formatMoney(outputs.privateCapitalAmount, opCur)} fieldKey="privateCapital" className="text-purple-700" />
              <Metric label="Net after fin." value={formatOp(outputs.netAfterFinancing)} fieldKey="netAfterFinancing" />
              <Metric
                label={`Partner take (${(outputs.pcProfitShare * 100).toFixed(0)}%)`}
                value={formatOp(outputs.profitShareAmount)}
                fieldKey="profitShareAmount"
                className="text-purple-700 font-bold"
              />
              <Metric label="Sponsor residual" value={formatOp(outputs.finalNetProfit)} fieldKey="finalNetProfit" className="font-bold" />
            </div>
          )}
        </div>
      )}

      {/* ═══ Detail: equal P&L | Funding & Capital ═══ */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-1 mb-1 md:mb-0.5 items-start">
        {/* Left — linear P&L */}
        <div className="bg-white border border-gray-200 rounded overflow-hidden min-w-0" data-testid="pl-rollout">
          <div className="px-1.5 py-1 border-b border-gray-100 flex justify-between items-center">
            <span className="text-[7px] font-bold text-gray-400 uppercase">P&amp;L</span>
            {showFxCaption ? (
              <span className="normal-case font-medium text-sky-700 tabular-nums text-[7px]" data-testid="pl-fx-caption">
                {money.fx.base}→CHF {Number(money.fx.rate).toFixed(3)}
              </span>
            ) : (
              <span className="normal-case text-gray-500 text-[7px]">{opCur}</span>
            )}
          </div>
          <Accordion type="multiple" defaultValue={['revenue', 'costs']} className="space-y-0">
            <AccordionItem value="revenue" className="border-0 border-b border-gray-100 rounded-none">
              <AccordionTrigger className="px-1.5 py-px text-[9px] font-bold hover:no-underline hover:bg-gray-50">
                1 · Revenue ({opCur})
              </AccordionTrigger>
              <AccordionContent className="px-1.5 pb-1">
                <div className={`${GRID} text-[6px] text-gray-400 mb-px`}>
                  <span /><span /><span className="text-right">{opCur}</span>
                </div>
                <Row label="Sales" value={outputs.salesRevenue} fieldKey="salesRevenue" currency={opCur} />
                <Row label="Parking" value={outputs.parkingRevenue} fieldKey="parkingRevenue" currency={opCur} />
                <TotalRow label="TOTAL REVENUE" value={outputs.totalRevenue} fieldKey="totalRevenue" currency={opCur} />
                {money.isFxActive && (
                  <div className="mt-0.5 px-1">
                    <ChfEquivalent value={report('totalRevenue')} formatReport={formatReport} testId="pl-revenue-chf" />
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="costs" className="border-0 border-b border-gray-100 rounded-none">
              <AccordionTrigger className="px-1.5 py-px text-[9px] font-bold hover:no-underline hover:bg-gray-50">
                2 · Cost breakdown ({opCur})
              </AccordionTrigger>
              <AccordionContent className="px-1.5 pb-1">
                <div className={`${GRID} text-[7px] text-gray-400 mb-0.5`}>
                  <div className="flex items-center justify-between">
                    <span>{canEdit ? `Edit % or ${opCur}` : opCur}</span>
                    {canEdit && costKeys.some(k => inputs.overrides?.[k]) && <ResetButton onClick={() => resetOverrides(costKeys)} />}
                  </div>
                  <span className="text-center">%</span>
                  <span className="text-right">{opCur}</span>
                </div>
                <SectionLabel>Land</SectionLabel>
                <Row label="Terrain" value={outputs.landCost} fieldKey="landCost" currency={opCur} />
                <EditableOverrideRow label="Notary" rate={outputs.notaryFeesRate} amount={outputs.notaryFees} fieldKey="notaryFees" onOverride={overrideHandler} sub baseName="Land" currency={opCur} />
                <EditableOverrideRow label="Other" rate={outputs.otherLandFeesRate} amount={outputs.otherLandFees} fieldKey="otherLandFees" onOverride={overrideHandler} sub baseName="Land" currency={opCur} />
                <TotalRow label="Land Total" value={outputs.totalLandCost} fieldKey="totalLandCost" currency={opCur} />
                <SectionLabel>Construction</SectionLabel>
                <EditableOverrideRow label="Build" rate={outputs.constructionRate} amount={outputs.constructionCost} fieldKey="constructionCost" onOverride={overrideHandler} baseName="Revenue" currency={opCur} />
                <EditableOverrideRow label="Reserve" rate={outputs.constructionReserveRate} amount={outputs.constructionReserve} fieldKey="constructionReserve" onOverride={overrideHandler} sub baseName="Construction" currency={opCur} />
                <TotalRow label="Build Total" value={outputs.totalConstructionCost} fieldKey="totalConstructionCost" currency={opCur} />
                <SectionLabel>Soft Costs</SectionLabel>
                <EditableOverrideRow label="Architecture" rate={outputs.architectureRate} amount={outputs.architectureCost} fieldKey="architectureCost" onOverride={overrideHandler} sub baseName="Build Total" currency={opCur} />
                <EditableOverrideRow label="Consultants" rate={outputs.consultantsRate} amount={outputs.consultantsCost} fieldKey="consultantsCost" onOverride={overrideHandler} sub baseName="Build Total" currency={opCur} />
                <EditableOverrideRow label="Contingency" rate={outputs.contingencyRate} amount={outputs.contingencyCost} fieldKey="contingencyCost" onOverride={overrideHandler} sub baseName="Build Total" currency={opCur} />
                <SectionLabel>Commercial</SectionLabel>
                <EditableOverrideRow label="Sales Cost" rate={outputs.commercializationRate} amount={outputs.commercializationCost} fieldKey="commercializationCost" onOverride={overrideHandler} sub baseName="Revenue" currency={opCur} />
                <EditableOverrideRow label="Pilotage" rate={outputs.pilotageRate} amount={outputs.pilotageCost} fieldKey="pilotageCost" onOverride={overrideHandler} sub baseName="PBT" currency={opCur} />
                <TotalRow label="DEV COST" value={outputs.totalDevCost} fieldKey="totalDevCost" currency={opCur} />
                {money.isFxActive && (
                  <div className="mt-0.5 mb-0.5 px-1">
                    <ChfEquivalent value={report('totalDevCost')} formatReport={formatReport} testId="pl-devcost-chf" />
                  </div>
                )}
              </AccordionContent>
            </AccordionItem>
          </Accordion>
          <PlProfitBox
            money={money}
            formatReport={formatReport}
            formatOp={formatOp}
            report={report}
            taxLabel={taxLabel}
            outputs={outputs}
            partnerShareActive={partnerShareActive}
          />
        </div>

        {/* Right — editable funding detail + P&L waterfall */}
        <div className="min-w-0 space-y-1" data-testid="pl-detail-columns">
            <Accordion
              type="multiple"
              value={fundingAccordion}
              onValueChange={setFundingAccordion}
              className="space-y-px min-w-0"
            >
              <AccordionItem value="funding" className="border border-gray-200 bg-white rounded overflow-hidden">
                <AccordionTrigger className="px-1.5 py-px text-[9px] font-bold hover:no-underline hover:bg-gray-50">
                  Edit Funding &amp; Capital
                </AccordionTrigger>
                <AccordionContent className="px-1.5 pb-1">
            <div className="flex items-center justify-between mb-0.5 gap-1 flex-wrap">
              <span className="text-[7px] text-gray-400 font-bold uppercase">Sources of Funds</span>
              <div className="flex items-center gap-1 flex-wrap justify-end">
                <span
                  className={`text-[7px] font-bold ${coverageOk ? 'text-emerald-600' : 'text-amber-600'}`}
                  title="Total funding ÷ total development cost (±5% = OK)"
                  data-testid="funding-coverage"
                >
                  Coverage: {(outputs.fundingCoverageRatio * 100).toFixed(0)}% vs Dev Cost
                </span>
                {canEdit && !coverageOk && outputs.totalDevCost > 0 && fillGapPlans.length > 0 && (
                  <button
                    type="button"
                    onClick={fillFundingGap}
                    className="text-[7px] font-semibold text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded px-1 py-px"
                    data-testid="fill-funding-gap"
                    title={`Adjust ${fillGapPrimaryLabel}${fillGapPlans.length > 1 ? ' (+ cascade)' : ''} so Total Funding equals Dev Cost`}
                  >
                    Fill gap {formatMoney(Math.abs(gapChf), opCur)}
                  </button>
                )}
                {canEdit && (
                  <>
                    <button
                      type="button"
                      onClick={useDevBasesForFunding}
                      className="text-[7px] text-gray-500 hover:text-gray-800 underline-offset-2 hover:underline"
                      data-testid="use-dev-funding-bases"
                      title="Set Equity / Partner / Bank bases to Dev Cost"
                    >
                      Use Dev base
                    </button>
                    <ResetButton
                      onClick={resetFundingAndCapital}
                      label="Reset funding"
                      title="Clears funding overrides and base selectors only — not land, price, or other deal inputs"
                    />
                  </>
                )}
              </div>
            </div>
            {!coverageOk && (
              <div className="text-[7px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-1 py-0.5 mb-1 leading-tight" data-testid="funding-coverage-hint">
                Each funding amount is % × its selected base (Land/Dev/Build/Soft). Coverage is always Total Funding ÷ Dev Cost
                ({(outputs.fundingCoverageRatio * 100).toFixed(0)}% now
                {outputs.totalDevCost > 0
                  ? ` — gap ${formatMoney(Math.abs(gapChf), opCur)}`
                  : ''}
                ). Use “Fill gap” to bring Total Funding to Dev Cost (Bank if already used, else Equity; surplus cascades Bank→Equity→Partner), or “Use Dev base” then edit %.
              </div>
            )}
            <div className={`${GRID} text-[6px] text-gray-400 mb-px`}>
              <span /><span className="text-center">%</span><span className="text-right">{opCur}</span>
            </div>
            <EditableOverrideRow label="Equity (you)" rate={outputs.equityRate} amount={outputs.equityRequired}
              fieldKey="equityRequired" onOverride={overrideHandler} baseName={SOURCE_LABELS[outputs.equityBaseSource]}
              sourceSelector={<SourceSelect value={inputs.capitalSources?.equity || 'landCost'} onChange={(s) => handleSourceChange('equity', s)} testId="source-equity" />}
              currency={opCur} />
            <EditableOverrideRow label={partnerLabel} rate={outputs.privateCapitalRate} amount={outputs.privateCapitalAmount}
              fieldKey="privateCapital" onOverride={overrideHandler} baseName={SOURCE_LABELS[outputs.privateCapitalBaseSource]}
              sourceSelector={<SourceSelect value={inputs.capitalSources?.privateCapital || 'landCost'} onChange={(s) => handleSourceChange('privateCapital', s)} testId="source-private" />}
              currency={opCur} />
            <EditableOverrideRow label="Bank" rate={outputs.bankDebtRate} amount={outputs.bankDebtAmount}
              fieldKey="bankDebt" onOverride={overrideHandler} baseName={SOURCE_LABELS[outputs.bankDebtBaseSource]}
              sourceSelector={<SourceSelect value={inputs.capitalSources?.bankDebt || inputs.capitalSources?.debt || 'landCost'} onChange={(s) => handleSourceChange('bankDebt', s)} testId="source-bank" />}
              currency={opCur} />
            {canEditAssumptions ? (
              <AssumptionMoneyRow
                label="Construction Facility"
                value={assumptions?.bankDebt?.constructionFacility ?? 0}
                currency={opCur}
                testId="pnl-construction-facility"
                onChange={(value) => onAssumptionsChange((prev) => (
                  applyBankDebtAssumption(prev, 'constructionFacility', Math.max(0, Number(value) || 0))
                ))}
              />
            ) : (
              <Row label="Construction Facility" value={outputs.constructionFacilityAmount} fieldKey="constructionFacilityAmount" />
            )}
            <TotalRow label="Total Funding" value={outputs.fundingTotal} fieldKey="fundingTotal" currency={opCur} />

            <div className="flex items-center justify-between" style={{ marginTop: '4px', marginBottom: '2px' }}>
              <span className="text-[7px] text-gray-400 font-bold uppercase">Capital Invested (at risk, {opCur})</span>
            </div>
            <Row label="Equity (you)" value={outputs.equityRequired} fieldKey="equityRequired" />
            <EditableOverrideRow label="Fees" rate={outputs.feesRate} amount={outputs.upfrontFees}
              fieldKey="upfrontFees" onOverride={overrideHandler} baseName={SOURCE_LABELS[outputs.feesBaseSource]}
              sourceSelector={<SourceSelect value={inputs.capitalSources?.fees || 'totalDevCost'} onChange={(s) => handleSourceChange('fees', s)} testId="source-fees" />}
              currency={opCur} />
            <Row label="Feasibility" value={outputs.feasibilityStudy} fieldKey="feasibilityStudy" sub />
            <Row label="Fin. Costs" value={outputs.totalFinancingCost} fieldKey="totalFinancingCost" sub />
            <TotalRow label="CAPITAL INVESTED" value={outputs.capitalInvested} fieldKey="capitalInvested" />
            <div className="text-[7px] text-gray-400 italic leading-none mt-px">
              Excludes debt/partner principal. Fin. Costs are listed here (at risk) and again under Cost of Capital (interest &amp; fees).
            </div>

            <div style={{ marginTop: '4px', marginBottom: '2px' }}>
              <span className="text-[7px] text-amber-600 font-bold uppercase">Cost of Capital ({opCur})</span>
              <span className="text-[6px] text-gray-400 ml-1">{outputs.financingLabel}</span>
            </div>
            {(outputs.bankDebtAmount > 0 || outputs.constructionFacilityAmount > 0) && (
              <Row label={`Bank Interest (${(outputs.bankInterestRate * 100).toFixed(1)}% x ${outputs.bankDurationMonths}mo)`} value={outputs.bankInterestCost} fieldKey="bankInterestCost" sub />
            )}
            {outputs.bankSetupCost > 0 && (
              <Row label="Bank Setup Fees" value={outputs.bankSetupCost} fieldKey="bankSetupCost" sub />
            )}
            {outputs.privateDebtCost > 0 && (
              <Row label="Private Interest" value={outputs.privateDebtCost} fieldKey="privateDebtCost" sub />
            )}
            {outputs.privateDebtFees > 0 && (
              <Row label="Private Fees" value={outputs.privateDebtFees} fieldKey="privateDebtFees" sub />
            )}
            {outputs.equityStructuringCost > 0 && (
              <Row label="Eq. Structuring" value={outputs.equityStructuringCost} fieldKey="equityStructuringCost" sub />
            )}
            <TotalRow label="Total Fin. Costs" value={outputs.totalFinancingCost} fieldKey="totalFinancingCost" />

            {partnerShareConfigured && (
              <>
                <div style={{ marginTop: '4px', marginBottom: '2px' }}>
                  <span className="text-[7px] text-purple-600 font-bold uppercase">Equity Partner Dilution</span>
                </div>
                {!hasPC ? (
                  <div className="text-[7px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-1 py-0.5 mb-1 leading-tight" data-testid="partner-share-inactive-detail">
                    Set partner {opCur} in Funding → Partner capital to activate {(outputs.pcProfitShare * 100).toFixed(0)}% share.
                    Dilution is after tax &amp; financing — not deducted from PBT.
                  </div>
                ) : (
                  <>
                    <Row label="Partner capital" value={outputs.privateCapitalAmount} fieldKey="privateCapital" sub className="text-purple-700" currency={opCur} />
                    <Row label="Net after Fin." value={outputs.netAfterFinancing} fieldKey="netAfterFinancing" sub />
                    <Row label={`Partner take (${(outputs.pcProfitShare * 100).toFixed(0)}%)`} value={outputs.profitShareAmount} fieldKey="profitShareAmount" sub className="text-purple-700" />
                    <TotalRow label="SPONSOR RESIDUAL NET" value={outputs.finalNetProfit} fieldKey="finalNetProfit" />
                    {split.total > 0 && (
                      <div className="text-[7px] text-purple-500 italic leading-none mt-px" data-testid="promote-vs-ownership-detail">
                        Promote {(outputs.pcProfitShare * 100).toFixed(0)}% of net after financing
                        {' '}· capital split you {(split.sponsorPct * 100).toFixed(0)}% / partner {(split.partnerPct * 100).toFixed(0)}%
                        {' '}(share is not ownership-weighted).
                      </div>
                    )}
                    <div className="text-[7px] text-purple-400 italic leading-none mt-px">Dilution / promote — not interest, not in Cost of Capital, does not reduce {taxLabel}.</div>
                  </>
                )}
              </>
            )}

            <div className="mt-1 pt-1 border-t border-gray-100">
              <div className="text-[7px] text-gray-400 italic leading-none">
                ROI on Capital at Risk = Final Net / Capital Invested = {formatPercent(outputs.roiOnEquity)}
              </div>
              <div className="text-[6px] text-gray-400 leading-none mt-px">
                Not project ROI (vs Dev Cost) or pure ROE — excludes debt/partner principal.
              </div>
            </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          <PlWaterfallChart
            outputs={outputs}
            currency={opCur}
            taxLabel={taxLabel}
          />
        </div>
      </div>

      {showZeroEquityCaveat && (
        <div className="mb-0.5 flex items-start gap-1 px-1 py-0.5 rounded text-[8px] bg-amber-50 text-amber-800 border border-amber-100" data-testid="zero-equity-partner-caveat">
          <WarningCircle weight="fill" className="w-2.5 h-2.5 shrink-0 mt-px" />
          <span>
            <span className="font-semibold">ROI caveat:</span> Equity (you) is {opCur} 0 while Partner capital funds the deal.
            ROI at risk uses fees + feasibility + fin. costs only — partner principal is excluded from the denominator.
          </span>
        </div>
      )}

      {warnings?.length > 0 && (
        <div className="mb-0.5 space-y-px" data-testid="warnings-section">
          {warnings.filter(w => w.type !== 'target').map((w, i) =>
              <div key={i} className={`flex items-center gap-1 px-1 py-px rounded text-[8px] ${
                w.severity === 'critical' ? 'bg-red-50 text-red-700' :
                w.severity === 'info' ? 'bg-gray-50 text-gray-500' :
                'bg-amber-50 text-amber-700'
              }`} data-testid={`warning-${w.severity}`}>
                <WarningCircle weight="fill" className="w-2.5 h-2.5 shrink-0" />
                <span className="font-semibold">{w.short}:</span>
                <span>{w.message}</span>
              </div>
          )}
        </div>
      )}

      {canEdit && hasOverrides && (
        <div className="mb-0.5 flex justify-end">
          <ResetButton onClick={resetAllOverrides} label="Reset All" />
        </div>
      )}

      {/* Notes — auto-expands downward to fit all text */}
      <div className="mt-1 bg-white border border-gray-200 rounded p-1.5 md:p-1" data-testid="notes-box">
        <div className="text-[8px] md:text-[7px] font-bold text-gray-400 uppercase tracking-wide mb-0.5">Notes</div>
        <textarea
          ref={notesRef}
          value={notes || ''}
          onChange={canEditNotes ? (e) => onNotesChange(e.target.value) : undefined}
          readOnly={!canEditNotes}
          rows={3}
          placeholder={canEditNotes ? 'Add notes about this analysis...' : 'No notes in this shared snapshot.'}
          aria-label="Analysis notes"
          aria-readonly={!canEditNotes || undefined}
          className={`w-full min-h-[60px] text-[12px] md:text-[10px] text-gray-700 border border-gray-200 rounded px-2 md:px-1.5 py-1.5 md:py-1 leading-snug focus:outline-none overflow-hidden ${
            canEditNotes
              ? 'bg-gray-50 resize-none focus:border-blue-300 focus:ring-1 focus:ring-blue-200'
              : 'bg-slate-100 resize-none cursor-default'
          }`}
          data-testid="notes-textarea"
        />
      </div>

      {!readOnly && <DocumentsSection dealId={dealId} onSave={onSave} />}
    </main>
  );
}
